import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, FileText, Plus, Truck } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { CustomersGateway } from '@forq/customers-data';
import { CustomerSearchField } from '../shared/CustomerSearchField';
import {
  DataTable,
  type DataTableColumn,
  EmptyState,
  FlowTrail,
  MetricTile,
  RecordField,
  RecordPanel,
  SearchSelect,
  StatusPill,
  type StatusTone,
  Tabs,
  type TabItem,
  CustomizableMetrics,
  WorkspacePage,
  confirmDiscard,
} from '@forq/ui';
import type { FinanceGateway } from '@forq/finance-data';
import { emptyCompanyProfile, type CompanyProfileGateway, type TenantCompanyProfile } from '@forq/platform-data';
import type { MachineAttributeValue, MachineTypeFieldRecord, MachinesGateway } from '@forq/machines-data';
import type { FleetMachine, RentalGateway } from '@forq/rental-data';
import {
  billingStatusLabels,
  computeFinalBilling,
  computeItemCharge,
  computeMonthlyBilling,
  intervalDates,
  findBookingClash,
  isMachineAvailable,
  rentalFlowTransitions,
  rentalStatusLabels,
  weekdayLabels,
  type RentalBillingStatus,
  type RentalMachineInput,
  type RentalOrder,
  type RentalStatus,
  type TransportStatus,
} from '@forq/rental-data';
import { buildRentalFlow } from './flow';
import { availableRentalDocuments, rentalDocumentLabels, renderRentalDocumentHtml, type RentalDocMachineInfo, type RentalDocumentKind } from './documents';

interface RentalPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly gateway: RentalGateway;
  readonly financeGateway: FinanceGateway;
  readonly customersGateway: CustomersGateway;
  readonly companyGateway: CompanyProfileGateway;
  readonly machinesGateway: MachinesGateway;
  readonly userName: string;
}

type RentalTab = 'overview' | 'orders' | 'dispatch' | 'calendar' | 'billing' | 'documents';

const statusTone: Record<RentalStatus, StatusTone> = {
  request: 'neutral', reserved: 'info', active: 'progress', return_scheduled: 'warning', completed: 'done', cancelled: 'danger',
};
const billingTone: Record<RentalBillingStatus, StatusTone> = { open: 'neutral', ready: 'warning', billed: 'done' };
const transportStatusLabels: Record<TransportStatus, string> = { open: 'Offen', scheduled: 'Geplant', in_transit: 'Unterwegs', done: 'Erledigt' };
// Der reale Kalendertag (einmal beim Laden). War ein hartcodiertes Demo-Datum ('2026-07-15') und
// hat damit auf dem Live-System ALLE „heute"-Bezüge verankert: Belegdatum, Default-Daten im
// Erfassen-Formular, „Transporte heute", Auslastung/„Flotte heute" und den Mietkalender.
// Gleiches Muster wie in den anderen Modulen (Kunden/Maschinen/Stammdaten).
const TODAY = new Date().toISOString().slice(0, 10);

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}
function dateLabel(value: string | null): string {
  return value ? new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`)) : '–';
}
function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang konnte nicht verarbeitet werden.';
}
function num(form: FormData, key: string): number {
  return Number(String(form.get(key) ?? '0').replace(',', '.')) || 0;
}
function str(form: FormData, key: string): string {
  return String(form.get(key) ?? '').trim();
}
function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function RentalPage({ tenant, mode, permissions, gateway, financeGateway, customersGateway, companyGateway, machinesGateway, userName }: RentalPageProps) {
  const [orders, setOrders] = useState<readonly RentalOrder[]>([]);
  const [fleet, setFleet] = useState<readonly FleetMachine[]>([]);
  const [tab, setTab] = useState<RentalTab>('overview');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RentalOrder | null>(null);
  const [returning, setReturning] = useState<RentalOrder | null>(null);
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [typeFields, setTypeFields] = useState<Record<string, readonly MachineTypeFieldRecord[]>>({});
  const [docView, setDocView] = useState<{ kind: RentalDocumentKind; order: RentalOrder } | null>(null);

  const canEdit = permissions.has('rental.edit');

  // Vom Startbild aus (?neu) direkt „Mietauftrag erfassen" öffnen -- ein Klick weniger.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('neu') === null || !canEdit) return;
    setCreating(true);
    setSearchParams((prev) => { prev.delete('neu'); return prev; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const canBill = permissions.has('rental.bill');
  const canReturn = permissions.has('rental.return');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Die vermietbaren Maschinen kommen aus dem Maschinenstamm (Status „Mietpark"), NICHT aus einer
      // separaten Fleet-Tabelle -- so hat der Mietpark echten Bezug zum Bestand. Sätze pro Auftrag.
      const [orderList, machinePage] = await Promise.all([
        gateway.listRentalOrders(tenant.id),
        machinesGateway.searchMachines({ tenantId: tenant.id, status: 'rental', pageSize: 500 }),
      ]);
      setOrders(orderList);
      setFleet(machinePage.items.map((m) => ({
        id: m.id, machineNumber: m.number, machineLabel: `${m.manufacturer} ${m.model}`.trim(),
        // Miet-Standardsätze aus dem Maschinenstamm; sie belegen den Mietauftrag vor (überschreibbar).
        dailyRate: m.rentalDailyRate, weeklyRate: m.rentalWeeklyRate, monthlyRate: m.rentalMonthlyRate,
        minimumDays: m.rentalMinimumDays || 1,
      })));
      setSelectedId((current) => current && orderList.some((o) => o.id === current) ? current : orderList[0]?.id ?? null);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [gateway, machinesGateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setTab('overview'); }, [tenant.id]);
  useEffect(() => { void companyGateway.loadProfile(tenant.id).then(setCompany).catch(() => setCompany(emptyCompanyProfile)); }, [companyGateway, tenant.id]);
  useEffect(() => {
    // Feld-Definitionen je Maschinentyp -- liefern Label + Einheit für die technischen Daten im Beleg.
    void machinesGateway.listMachineTypes(tenant.id)
      .then((types) => setTypeFields(Object.fromEntries(types.map((t) => [t.id, t.fields]))))
      .catch(() => setTypeFields({}));
  }, [machinesGateway, tenant.id]);

  const selected = selectedId ? orders.find((o) => o.id === selectedId) ?? null : null;
  const patch = useCallback((updated: RentalOrder) => setOrders((current) => current.map((o) => o.id === updated.id ? updated : o)), []);

  const metrics = useMemo(() => {
    const active = orders.filter((o) => o.status === 'active');
    const rentedMachines = new Set(active.flatMap((o) => o.items.map((i) => i.machineNumber)));
    const transportsToday = orders.flatMap((o) => o.transports).filter((t) => t.plannedDate === TODAY && t.status !== 'done').length;
    // Flotte = als Mietpark markierte Maschinen PLUS alle aktuell vermieteten (deren Stammstatus kann
    // während der Miete abweichen). So bleibt Auslastung = vermietet / Gesamtflotte und nie über 100 %.
    const fleetTotal = new Set([...fleet.map((machine) => machine.machineNumber), ...rentedMachines]);
    return {
      utilization: fleetTotal.size ? Math.round((rentedMachines.size / fleetTotal.size) * 100) : 0,
      active: active.length,
      transportsToday,
      billingOpen: orders.filter((o) => o.billingStatus === 'ready').length,
      requests: orders.filter((o) => o.status === 'request').length,
    };
  }, [orders, fleet]);

  const advance = useCallback(async (order: RentalOrder, toStatus: RentalStatus) => {
    if (toStatus === 'completed') { setReturning(order); return; }
    setBusy(true); setError(null);
    try { patch(await gateway.transitionRental({ tenantId: tenant.id, rentalId: order.id, toStatus })); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }, [gateway, patch, tenant.id]);

  const terminate = useCallback(async (order: RentalOrder) => {
    const date = window.prompt('Miete vorzeitig beenden zum (JJJJ-MM-TT):', TODAY)?.trim();
    if (!date) return;
    setBusy(true); setError(null);
    try { patch(await gateway.terminateEarly({ tenantId: tenant.id, rentalId: order.id, terminationDate: date })); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }, [gateway, patch, tenant.id]);

  const dispatchTransport = useCallback(async (order: RentalOrder, transportId: string, change: { driverName?: string; status?: TransportStatus }) => {
    setBusy(true); setError(null);
    try { patch(await gateway.updateTransport({ tenantId: tenant.id, rentalId: order.id, transportId, ...change })); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }, [gateway, patch, tenant.id]);

  const submitOrder = useCallback(async (event: FormEvent<HTMLFormElement>, machines: readonly RentalMachineInput[]) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (!str(form, 'customerNumber')) { setError('Bitte einen Kunden aus dem Kundenstamm auswählen.'); return; }
    if (machines.length === 0) { setError('Bitte mindestens eine Maschine wählen.'); return; }
    const base = {
      tenantId: tenant.id, customerNumber: str(form, 'customerNumber'), customerName: str(form, 'customerName'),
      startDate: str(form, 'startDate'), endDate: str(form, 'endDate'), siteAddress: str(form, 'siteAddress'),
      contactName: str(form, 'contactName'), contactPhone: str(form, 'contactPhone'),
      deliveryRequired: form.get('deliveryRequired') === 'on', pickupRequired: form.get('pickupRequired') === 'on',
      depositAmount: num(form, 'depositAmount'), note: str(form, 'note'), machines,
      kind: form.get('kind') === 'interval' ? ('interval' as const) : ('standard' as const),
      intervalWeekday: Number(form.get('intervalWeekday') ?? 5),
      transportCost: num(form, 'transportCost'),
      asOffer: form.get('asOffer') === 'on',
    };
    setBusy(true); setError(null);
    try {
      const saved = editing
        ? await gateway.updateRentalOrder({ ...base, rentalId: editing.id })
        : await gateway.createRentalOrder(base);
      setCreating(false); setEditing(null); await load(); setSelectedId(saved.id); setTab('orders');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }, [editing, gateway, load, tenant.id]);

  const submitReturn = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!returning) return;
    const form = new FormData(event.currentTarget);
    setBusy(true); setError(null);
    try {
      const updated = await gateway.captureReturn({
        tenantId: tenant.id, rentalId: returning.id, actualReturnDate: str(form, 'actualReturnDate'),
        items: returning.items.map((item) => ({ itemId: item.id, meterIn: num(form, `meter-${item.id}`) || null, damageNote: str(form, `damage-${item.id}`), damageAmount: num(form, `damageAmount-${item.id}`) })),
      });
      patch(updated); setReturning(null);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }, [gateway, patch, returning, tenant.id]);

  const bill = useCallback(async (order: RentalOrder, cycle: 'month' | 'final') => {
    setBusy(true); setError(null); setNotice(null);
    try {
      // Rechnung ZUERST aus der reinen Berechnung; erst wenn sie steht, wird der Abrechnungsstand
      // fortgeschrieben. Vorher war es umgekehrt -- schlug Finance fehl, galt der Zeitraum als
      // abgerechnet, ohne dass je eine Rechnung entstand (stilles Umsatzleck).
      const billing = cycle === 'month' ? computeMonthlyBilling(order).billing : computeFinalBilling(order);
      // Warengruppe je abgerechneter Maschine auflösen (nur die Belegmaschinen, kein Flotten-weiter Abruf),
      // damit die Auswertung „Umsatz nach Warengruppe" den echten Stamm statt der groben Positionsart zeigt.
      const machineNumbers = [...new Set(billing.lines.map((line) => line.machineNumber).filter((n): n is string => Boolean(n)))];
      const warengruppeByNumber = new Map<string, { id: string | null; name: string }>();
      await Promise.all(machineNumbers.map(async (number) => {
        const fleetMachine = fleet.find((entry) => entry.machineNumber === number);
        if (!fleetMachine?.id) return;
        try {
          const detail = await machinesGateway.getMachine(tenant.id, fleetMachine.id);
          warengruppeByNumber.set(number, { id: detail.warengruppeId ?? null, name: detail.warengruppeName ?? '' });
        } catch { /* Warengruppe ist nachrangig -- ohne sie greift der kind-Fallback in der Auswertung. */ }
      }));
      const lines = billing.lines.map(({ machineNumber, ...line }) => {
        const warengruppe = machineNumber ? warengruppeByNumber.get(machineNumber) : undefined;
        return warengruppe ? { ...line, warengruppeId: warengruppe.id, warengruppeName: warengruppe.name } : line;
      });
      await financeGateway.createInvoice({
        tenantId: tenant.id,
        customerNumber: billing.customerNumber,
        customerName: billing.customerName,
        sourceType: 'rental',
        // Herkunft periodengenau: Monatsrechnungen bleiben erlaubt, dieselbe Periode wird nicht doppelt berechnet.
        sourceNumber: `${billing.rentalNumber} · ${billing.periodLabel}`,
        // Leistungsdatum (§ 14 UStG): bei der Miete ist es der abgerechnete Zeitraum.
        serviceDate: billing.periodFrom,
        serviceEndDate: billing.periodTo,
        lines,
      });
      const { order: updated } = cycle === 'month'
        ? await gateway.billMonth({ tenantId: tenant.id, rentalId: order.id })
        : await gateway.billFinal({ tenantId: tenant.id, rentalId: order.id });
      patch(updated);
      setNotice(`Rechnungsentwurf erstellt (${billing.periodLabel}) – jetzt in FORQ Finance sichtbar.`);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }, [financeGateway, gateway, machinesGateway, fleet, patch, tenant.id]);

  const tabs: readonly TabItem[] = [
    { id: 'overview', label: 'Übersicht' },
    { id: 'orders', label: 'Mietaufträge' },
    { id: 'dispatch', label: 'Disposition', badge: metrics.transportsToday || undefined },
    { id: 'calendar', label: 'Mietkalender' },
    { id: 'billing', label: 'Abrechnung', badge: metrics.billingOpen || undefined },
    { id: 'documents', label: 'Dokumente' },
  ];

  const orderColumns: readonly DataTableColumn<RentalOrder>[] = [
    { key: 'number', header: 'Mietauftrag', width: 'minmax(0, 1fr)', render: (o) => <span className="forq-stack"><strong>{o.number}</strong><small>{o.customerName}</small></span> },
    { key: 'machines', header: 'Maschinen', width: 'minmax(0, 1.2fr)', render: (o) => <span className="forq-stack"><strong>{o.items.length === 1 ? o.items[0].machineLabel : `${o.items.length} Maschinen`}</strong><small>{dateLabel(o.startDate)} – {dateLabel(o.endDate)}</small></span> },
    { key: 'status', header: 'Status', render: (o) => <StatusPill label={rentalStatusLabels[o.status]} tone={statusTone[o.status]} /> },
    { key: 'billing', header: 'Abrechnung', render: (o) => <StatusPill label={billingStatusLabels[o.billingStatus]} tone={billingTone[o.billingStatus]} /> },
  ];

  const transitions = selected ? rentalFlowTransitions(selected, permissions) : [];

  return (
    <WorkspacePage
      customizeKey="rental"
      eyebrow={`FORQ Mietpark · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Mietpark"
      description="Vermietung und Verfügbarkeit – Anfrage, Reservierung, Auslieferung, Rücknahme, Schäden und zentrale Abrechnung."
      actions={canEdit ? <button type="button" className="rental-btn" onClick={() => { setCreating(true); setError(null); }}><Plus size={15} /> Mietauftrag erfassen</button> : undefined}
      metrics={(
        <CustomizableMetrics
          pageKey="rental"
          tiles={[
            { key: 'utilization', node: <MetricTile label="Auslastung" value={`${metrics.utilization} %`} hint="Flotte aktuell vermietet" tone="info" /> },
            { key: 'active', node: <MetricTile label="Aktive Mieten" value={metrics.active} hint="laufend" /> },
            { key: 'transports', node: <MetricTile label="Transporte heute" value={metrics.transportsToday} hint="offen" tone={metrics.transportsToday > 0 ? 'warning' : 'default'} /> },
            { key: 'billing', node: <MetricTile label="Abrechnung offen" value={metrics.billingOpen} hint="Schlussabrechnung" tone={metrics.billingOpen > 0 ? 'warning' : 'default'} /> },
          ]}
        />
      )}
      toolbar={<Tabs items={tabs} activeId={tab} onSelect={(id) => setTab(id as RentalTab)} ariaLabel="Mietpark-Bereiche" />}
    >
      {error && !creating && !editing && <div className="rental-error">{error}</div>}
      {notice && <div className="rental-notice">{notice}</div>}

      {tab === 'overview' && <OverviewTab orders={orders} fleet={fleet} loading={loading} onOpen={(id) => { setSelectedId(id); setTab('orders'); }} />}

      {tab === 'orders' && (
        <>
          <DataTable columns={orderColumns} rows={orders} rowKey={(o) => o.id} onRowSelect={(o) => setSelectedId(o.id)} isRowSelected={(o) => selected?.id === o.id} loading={loading} emptyLabel="Keine Mietaufträge." ariaLabel="Mietaufträge" />
          {selected && (
            <RecordPanel
              eyebrow={`Mietauftrag · ${dateLabel(selected.startDate)} – ${dateLabel(selected.endDate)}`}
              title={`${selected.number} · ${selected.customerName}`}
              subtitle={`${selected.siteAddress || 'ohne Einsatzort'}${selected.contactName ? ` · ${selected.contactName}` : ''}`}
              status={<span className="rental-pills"><StatusPill label={rentalStatusLabels[selected.status]} tone={statusTone[selected.status]} /><StatusPill label={billingStatusLabels[selected.billingStatus]} tone={billingTone[selected.billingStatus]} /></span>}
            >
              <FlowTrail steps={buildRentalFlow(selected)} ariaLabel={`Mietstatus ${selected.number}`} />
              {(transitions.length > 0 || (canEdit && (selected.status === 'active' || selected.status === 'reserved'))) && (
                <div className="rental-actions">
                  {canEdit && (selected.status === 'request' || selected.status === 'reserved') && (
                    <button type="button" className="rental-btn" disabled={busy} onClick={() => { setEditing(selected); setError(null); }}>Bearbeiten</button>
                  )}
                  {transitions.map((t) => <button key={t.toStatus} type="button" className={`rental-btn${t.tone === 'danger' ? ' is-danger' : ''}`} disabled={busy} onClick={() => void advance(selected, t.toStatus)}>{t.label}</button>)}
                  {canEdit && (selected.status === 'active' || selected.status === 'reserved') && (
                    <button type="button" className="rental-btn is-danger" disabled={busy} onClick={() => void terminate(selected)}>Vorzeitig beenden</button>
                  )}
                </div>
              )}
              <div className="forq-record-fields">
                <RecordField label="Kaution" value={money(selected.depositAmount)} />
                <RecordField label="Auslieferung" value={selected.deliveryRequired ? 'erforderlich' : 'Selbstabholung'} />
                <RecordField label="Rückholung" value={selected.pickupRequired ? 'erforderlich' : 'Selbstanlieferung'} />
                <RecordField label="Bereits abgerechnet bis" value={dateLabel(selected.billedThrough)} />
              </div>
              {selected.kind === 'interval' && selected.intervalWeekday !== null && (
                <div className="rental-interval">
                  <strong>Intervallmiete</strong> · jeden {weekdayLabels[selected.intervalWeekday]} · {intervalDates(selected.startDate, selected.endDate, selected.intervalWeekday).length} Einsätze · Transport {money(selected.transportCost)}/Fahrt
                  <span className="rental-interval__next">Nächste: {intervalDates(TODAY, selected.endDate, selected.intervalWeekday).slice(0, 5).map((d) => dateLabel(d)).join(' · ') || 'keine mehr'}</span>
                </div>
              )}
              <RentalItemsTable order={selected} />
              <DocumentButtons order={selected} onOpen={(kind) => setDocView({ kind, order: selected })} />
              {selected.note && <p className="rental-note">{selected.note}</p>}
            </RecordPanel>
          )}
        </>
      )}

      {tab === 'dispatch' && <DispatchTab orders={orders} loading={loading} busy={busy} canEdit={permissions.has('rental.dispatch')} onUpdate={dispatchTransport} />}
      {tab === 'calendar' && <CalendarTab orders={orders} fleet={fleet} />}
      {tab === 'billing' && <BillingTab orders={orders} loading={loading} busy={busy} canBill={canBill} onBill={bill} />}
      {tab === 'documents' && <DocumentsTab orders={orders} onOpen={(kind, order) => setDocView({ kind, order })} />}

      {(creating || editing) && <CreateModal editOrder={editing} fleet={fleet} orders={orders} busy={busy} error={error} tenantId={tenant.id} customersGateway={customersGateway} onClose={() => { setCreating(false); setEditing(null); }} onSubmit={submitOrder} />}
      {returning && canReturn && <ReturnModal order={returning} busy={busy} onClose={() => setReturning(null)} onSubmit={submitReturn} />}
      {docView && <RentalDocumentModal kind={docView.kind} order={docView.order} company={company} employeeName={userName} machinesGateway={machinesGateway} customersGateway={customersGateway} typeFields={typeFields} tenantId={tenant.id} onClose={() => setDocView(null)} />}
    </WorkspacePage>
  );
}

function RentalItemsTable({ order }: { readonly order: RentalOrder }) {
  return (
    <div className="rental-items">
      <div className="rental-items__row rental-items__row--head"><span>Maschine</span><span>Satz</span><span>Betriebsstunden</span><span>Miete gesamt</span></div>
      {order.items.map((item) => (
        <div className="rental-items__row" key={item.id}>
          <span className="rental-items__machine">{item.machineLabel}</span>
          <span>{money(item.dailyRate)}/Tag{item.weeklyRate ? ` · ${money(item.weeklyRate)}/Wo` : ''}{item.monthlyRate ? ` · ${money(item.monthlyRate)}/Mo` : ''}</span>
          <span>{item.meterOut ?? '–'} → {item.meterIn ?? '…'}{item.damageAmount > 0 ? ` · Schaden ${money(item.damageAmount)}` : ''}</span>
          <span className="rental-items__amount">{money(order.kind === 'interval' && order.intervalWeekday !== null
            ? intervalDates(order.startDate, order.endDate, order.intervalWeekday).length * item.dailyRate
            : computeItemCharge(item, item.startDate, item.actualReturnDate ?? item.endDate))}</span>
        </div>
      ))}
    </div>
  );
}

function OverviewTab({ orders, fleet, loading, onOpen }: { readonly orders: readonly RentalOrder[]; readonly fleet: readonly FleetMachine[]; readonly loading: boolean; readonly onOpen: (id: string) => void }) {
  if (loading) return <EmptyState eyebrow="Mietpark" title="Lädt …" description="Einen Moment, die Mietdaten werden geladen." />;
  const upcoming = orders.filter((o) => o.status !== 'completed' && o.status !== 'cancelled').slice(0, 6);
  const freeToday = fleet.filter((m) => isMachineAvailable(orders, m.machineNumber, TODAY, TODAY)).length;
  return (
    <div className="rental-overview">
      <section className="rental-card">
        <h3>Nächste Mietvorgänge</h3>
        {upcoming.length === 0 ? <p className="rental-muted">Keine offenen Mietvorgänge.</p> : upcoming.map((o) => (
          <button type="button" key={o.id} className="rental-list-item" onClick={() => onOpen(o.id)}>
            <span className="forq-stack"><strong>{o.number} · {o.items[0]?.machineLabel ?? '–'}</strong><small>{o.customerName} · {dateLabel(o.startDate)} – {dateLabel(o.endDate)}</small></span>
            <StatusPill label={rentalStatusLabels[o.status]} tone={statusTone[o.status]} />
          </button>
        ))}
      </section>
      <section className="rental-card">
        <h3>Flotte heute</h3>
        <p className="rental-big">{freeToday} <small>von {fleet.length} frei</small></p>
        {fleet.map((m) => {
          const free = isMachineAvailable(orders, m.machineNumber, TODAY, TODAY);
          return <div className="rental-fleet-row" key={m.machineNumber}><span>{m.machineLabel}</span><StatusPill label={free ? 'Verfügbar' : 'Vermietet'} tone={free ? 'done' : 'progress'} /></div>;
        })}
      </section>
    </div>
  );
}

function DispatchTab({ orders, loading, busy, canEdit, onUpdate }: { readonly orders: readonly RentalOrder[]; readonly loading: boolean; readonly busy: boolean; readonly canEdit: boolean; readonly onUpdate: (order: RentalOrder, transportId: string, change: { driverName?: string; status?: TransportStatus }) => void }) {
  if (loading) return <EmptyState eyebrow="Disposition" title="Lädt …" description="Einen Moment, die Mietdaten werden geladen." />;
  const rows = orders.flatMap((o) => o.transports.filter((t) => t.status !== 'done').map((t) => ({ order: o, transport: t })));
  return (
    <div className="rental-dispatch">
      {(['delivery', 'pickup'] as const).map((type) => (
        <section className="rental-card" key={type}>
          <h3>{type === 'delivery' ? 'Auslieferung' : 'Abholung'}</h3>
          {rows.filter((r) => r.transport.type === type).length === 0 ? <p className="rental-muted">Nichts geplant.</p> : rows.filter((r) => r.transport.type === type).map(({ order, transport }) => (
            <div className="rental-dispatch-item" key={transport.id}>
              <span className="forq-stack"><strong>{transport.number} · {order.items[0]?.machineLabel ?? '–'}</strong><small>{order.customerName} · {dateLabel(transport.plannedDate)}</small></span>
              <input aria-label="Fahrer" defaultValue={transport.driverName} placeholder="Fahrer" disabled={!canEdit || busy} onBlur={(e) => e.target.value !== transport.driverName && onUpdate(order, transport.id, { driverName: e.target.value })} />
              <SearchSelect ariaLabel="Status" value={transport.status} disabled={!canEdit || busy} onChange={(next) => onUpdate(order, transport.id, { status: next as TransportStatus })} options={(['open', 'scheduled', 'in_transit', 'done'] as const).map((s) => ({ value: s, label: transportStatusLabels[s] }))} />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function CalendarTab({ orders, fleet }: { readonly orders: readonly RentalOrder[]; readonly fleet: readonly FleetMachine[] }) {
  const days = Array.from({ length: 14 }, (_, index) => addDays(TODAY, index));
  return (
    <div className="rental-calendar-wrap">
      <div className="rental-calendar" style={{ gridTemplateColumns: `minmax(160px, 1.4fr) repeat(${days.length}, 1fr)` }}>
        <span className="rental-calendar__corner">Maschine</span>
        {days.map((day) => <span key={day} className="rental-calendar__day">{day.slice(8)}.{day.slice(5, 7)}</span>)}
        {fleet.map((machine) => (
          <RentalCalendarRow key={machine.machineNumber} machine={machine} days={days} orders={orders} />
        ))}
      </div>
    </div>
  );
}

function RentalCalendarRow({ machine, days, orders }: { readonly machine: FleetMachine; readonly days: readonly string[]; readonly orders: readonly RentalOrder[] }) {
  return (
    <>
      <span className="rental-calendar__machine">{machine.machineLabel}</span>
      {days.map((day) => {
        const busy = !isMachineAvailable(orders, machine.machineNumber, day, day);
        return <span key={day} className={`rental-calendar__cell${busy ? ' is-busy' : ''}`} title={busy ? 'vermietet' : 'frei'} />;
      })}
    </>
  );
}

function BillingTab({ orders, loading, busy, canBill, onBill }: { readonly orders: readonly RentalOrder[]; readonly loading: boolean; readonly busy: boolean; readonly canBill: boolean; readonly onBill: (order: RentalOrder, mode: 'month' | 'final') => void }) {
  if (loading) return <EmptyState eyebrow="Abrechnung" title="Lädt …" description="Einen Moment, die Mietdaten werden geladen." />;
  const billable = orders.filter((o) => o.billingStatus !== 'billed' && o.status !== 'cancelled' && o.status !== 'request');
  if (billable.length === 0) return <EmptyState eyebrow="Abrechnung" title="Nichts abzurechnen." description="Laufende und zurückgenommene Mieten erscheinen hier." />;
  return (
    <div className="rental-billing">
      {billable.map((o) => (
        <div className="rental-billing-item" key={o.id}>
          <span className="forq-stack"><strong>{o.number} · {o.customerName}</strong><small>{o.items[0]?.machineLabel ?? '–'} · abgerechnet bis {dateLabel(o.billedThrough)}</small></span>
          <StatusPill label={billingStatusLabels[o.billingStatus]} tone={billingTone[o.billingStatus]} />
          <div className="rental-actions">
            {o.status === 'active' && <button type="button" className="rental-btn" disabled={!canBill || busy} onClick={() => onBill(o, 'month')}>Monatsabrechnung</button>}
            {o.billingStatus === 'ready' && <button type="button" className="rental-btn is-pay" disabled={!canBill || busy} onClick={() => onBill(o, 'final')}>Schlussabrechnung</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentButtons({ order, onOpen }: { readonly order: RentalOrder; readonly onOpen: (kind: RentalDocumentKind) => void }) {
  const docs = availableRentalDocuments(order);
  if (docs.length === 0) return null;
  return (
    <span className="rental-doc-papers">
      {docs.map((kind) => (
        <button key={kind} type="button" className="rental-chip" onClick={() => onOpen(kind)}>
          <FileText size={12} /> {rentalDocumentLabels[kind]}
        </button>
      ))}
    </span>
  );
}

function DocumentsTab({ orders, onOpen }: { readonly orders: readonly RentalOrder[]; readonly onOpen: (kind: RentalDocumentKind, order: RentalOrder) => void }) {
  const relevant = orders.filter((o) => o.status !== 'cancelled');
  if (relevant.length === 0) return <EmptyState eyebrow="Dokumente" title="Keine Belege." description="Sobald ein Mietauftrag erfasst ist, erscheinen hier Angebot, Auftragsbestätigung/Vertrag, Lieferschein und Rücknahmeschein." />;
  return (
    <div className="rental-documents">
      <div className="rental-notice">Belege öffnen als druckfertige Ansicht mit Ihrem Firmenkopf (aus Verwaltung → Firmenprofil) – dort dann „Drucken / als PDF speichern".</div>
      {relevant.map((o) => (
        <div className="rental-doc-item" key={o.id}>
          <span className="forq-stack"><strong>{o.number} · {rentalStatusLabels[o.status]}</strong><small>{o.customerName}</small></span>
          <DocumentButtons order={o} onOpen={(kind) => onOpen(kind, o)} />
        </div>
      ))}
    </div>
  );
}

function formatAttr(value: MachineAttributeValue, unit: string): string {
  if (value === null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  const text = typeof value === 'number' ? new Intl.NumberFormat('de-DE').format(value) : String(value);
  return unit ? `${text} ${unit}` : text;
}

function RentalDocumentModal({ kind, order, company, employeeName, machinesGateway, customersGateway, typeFields, tenantId, onClose }: {
  readonly kind: RentalDocumentKind; readonly order: RentalOrder; readonly company: TenantCompanyProfile;
  readonly employeeName: string; readonly machinesGateway: MachinesGateway; readonly customersGateway: CustomersGateway;
  readonly typeFields: Record<string, readonly MachineTypeFieldRecord[]>; readonly tenantId: string; readonly onClose: () => void;
}) {
  const [data, setData] = useState<{ machines: Record<string, RentalDocMachineInfo>; addressLines: readonly string[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [machineEntries, addressLines] = await Promise.all([
        Promise.all(order.items.map(async (item) => {
          try {
            const page = await machinesGateway.searchMachines({ tenantId, query: item.machineNumber, pageSize: 5 });
            const record = page.items.find((r) => r.number === item.machineNumber);
            if (!record) return null;
            const detail = await machinesGateway.getMachine(tenantId, record.id);
            const specs = (typeFields[detail.typeId] ?? [])
              .map((field) => ({ label: field.label, value: formatAttr(detail.attributes[field.key] ?? null, field.unit) }))
              .filter((spec) => spec.value !== '');
            const info: RentalDocMachineInfo = {
              number: detail.number, manufacturer: detail.manufacturer, model: detail.model, serialNumber: detail.serialNumber,
              constructionYear: detail.constructionYear, typeName: detail.typeName, location: detail.location, imageUrl: detail.imageUrl, specs,
            };
            return [item.machineNumber, info] as const;
          } catch { return null; }
        })),
        (async (): Promise<readonly string[]> => {
          // Kundenadresse aus dem Stamm (Nummer -> Datensatz -> primäre Anschrift).
          try {
            const page = await customersGateway.searchCustomers({ tenantId, query: order.customerNumber, pageSize: 5 });
            const record = page.items.find((c) => c.number === order.customerNumber);
            if (!record) return [];
            const detail = await customersGateway.getCustomer(tenantId, record.id);
            const address = detail.addresses.find((a) => a.primary) ?? detail.addresses[0];
            return address ? [address.street, `${address.postalCode} ${address.city}`.trim()].filter(Boolean) : [];
          } catch { return []; }
        })(),
      ]);
      if (!cancelled) {
        setData({
          machines: Object.fromEntries(machineEntries.filter((e): e is readonly [string, RentalDocMachineInfo] => e !== null)),
          addressLines,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [machinesGateway, customersGateway, typeFields, order, tenantId]);

  const html = useMemo(
    () => data ? renderRentalDocumentHtml(kind, { order, company, todayIso: TODAY, employeeName, customerAddressLines: data.addressLines, machines: data.machines }) : '',
    [data, kind, order, company, employeeName],
  );

  return (
    <div className="rental-modal rental-doc-modal" role="dialog" aria-modal="true" aria-label={rentalDocumentLabels[kind]} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rental-doc-modal__panel">
        <header>
          <span className="rental-doc-modal__title"><FileText size={15} /> {rentalDocumentLabels[kind]} · {order.number}</span>
          <button type="button" className="rental-btn" onClick={onClose}>Schließen</button>
        </header>
        {data
          ? <iframe className="rental-doc-frame" title={`${rentalDocumentLabels[kind]} ${order.number}`} srcDoc={html} />
          : <div className="rental-doc-loading">Beleg wird vorbereitet …</div>}
      </div>
    </div>
  );
}

function CreateModal({ editOrder, fleet, orders, busy, error, tenantId, customersGateway, onClose, onSubmit }: { readonly editOrder: RentalOrder | null; readonly fleet: readonly FleetMachine[]; readonly orders: readonly RentalOrder[]; readonly busy: boolean; readonly error: string | null; readonly tenantId: string; readonly customersGateway: CustomersGateway; readonly onClose: () => void; readonly onSubmit: (event: FormEvent<HTMLFormElement>, machines: readonly RentalMachineInput[]) => void }) {
  const [start, setStart] = useState(editOrder?.startDate ?? addDays(TODAY, 3));
  const [end, setEnd] = useState(editOrder?.endDate ?? addDays(TODAY, 17));
  const [customer, setCustomer] = useState<{ number: string; name: string } | null>(editOrder ? { number: editOrder.customerNumber, name: editOrder.customerName } : null);
  const [interval, setInterval] = useState(editOrder?.kind === 'interval');
  const [weekday, setWeekday] = useState(editOrder?.intervalWeekday ?? 5);
  const [asOffer, setAsOffer] = useState(false);
  // Gewählte Maschinen samt der (aus Flotte bzw. beim Bearbeiten aus dem Auftrag) vorbelegten, überschreibbaren Sätze.
  const [picked, setPicked] = useState<Record<string, RentalMachineInput>>(() =>
    editOrder ? Object.fromEntries(editOrder.items.map((item) => [item.machineNumber, {
      machineNumber: item.machineNumber, machineLabel: item.machineLabel,
      dailyRate: item.dailyRate, weeklyRate: item.weeklyRate, monthlyRate: item.monthlyRate,
    }])) : {});

  const toggle = (machine: FleetMachine, on: boolean) => setPicked((current) => {
    const next = { ...current };
    if (on) next[machine.machineNumber] = { machineNumber: machine.machineNumber, machineLabel: machine.machineLabel, dailyRate: machine.dailyRate, weeklyRate: machine.weeklyRate, monthlyRate: machine.monthlyRate };
    else delete next[machine.machineNumber];
    return next;
  });
  const setRate = (machineNumber: string, field: 'dailyRate' | 'weeklyRate' | 'monthlyRate', value: number) =>
    setPicked((current) => current[machineNumber] ? { ...current, [machineNumber]: { ...current[machineNumber], [field]: value } } : current);

  const machines = Object.values(picked);
  const chargeFor = (m: RentalMachineInput, minimumDays: number) =>
    computeItemCharge({ dailyRate: m.dailyRate, weeklyRate: m.weeklyRate, monthlyRate: m.monthlyRate, minimumDays }, start, end);
  const estimatedTotal = machines.reduce((sum, m) => sum + chargeFor(m, fleet.find((f) => f.machineNumber === m.machineNumber)?.minimumDays ?? 1), 0);

  return (
    <div className="rental-modal" role="dialog" aria-modal="true" aria-label={editOrder ? 'Mietauftrag bearbeiten' : 'Mietauftrag erfassen'} onMouseDown={(e) => { if (e.target === e.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="rental-modal__panel" onSubmit={(event) => onSubmit(event, machines)}>
        <header><CalendarClock size={16} /> <h2>{editOrder ? `Mietauftrag bearbeiten · ${editOrder.number}` : 'Mietauftrag erfassen'}</h2></header>
        <label className="rental-modal__wide">Kunde
          <CustomerSearchField tenantId={tenantId} gateway={customersGateway} onSelect={setCustomer} autoFocus={!editOrder} initialLabel={editOrder ? `${editOrder.customerNumber} · ${editOrder.customerName}` : undefined} />
        </label>
        <input type="hidden" name="customerNumber" value={customer?.number ?? ''} readOnly />
        <input type="hidden" name="customerName" value={customer?.name ?? ''} readOnly />
        <div className="rental-modal__grid">
          <label>Mietbeginn<input name="startDate" type="date" required value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label>Mietende<input name="endDate" type="date" required value={end} onChange={(e) => setEnd(e.target.value)} /></label>
          <label>Einsatzort<input name="siteAddress" defaultValue={editOrder?.siteAddress ?? ''} /></label>
          <label>Ansprechpartner<input name="contactName" defaultValue={editOrder?.contactName ?? ''} /></label>
          <label>Telefon<input name="contactPhone" defaultValue={editOrder?.contactPhone ?? ''} /></label>
          <label>Kaution €<input name="depositAmount" type="number" min="0" step="1" defaultValue={editOrder ? String(editOrder.depositAmount) : '0'} /></label>
          <label>{interval ? 'Transport €/Fahrt' : 'Transport €'}<input name="transportCost" type="number" min="0" step="1" defaultValue={editOrder ? String(editOrder.transportCost) : '0'} /></label>
        </div>
        <fieldset className="rental-fleet-pick">
          <legend>Maschinen & Miete (freie im Zeitraum wählbar)</legend>
          {fleet.map((m) => {
            // Mit Art und Wochentag der ANFRAGE: eine Freitags-Intervallmiete sperrt keine
            // Dienstagsmiete mehr. Und beim Bearbeiten zaehlt der eigene Auftrag nicht als Beleger --
            // vorher erschienen dort die eigenen Maschinen als belegt.
            const beleger = findBookingClash(
              orders, m.machineNumber,
              { kind: interval ? 'interval' : 'standard', weekday: interval ? weekday : null, from: start, to: end },
              editOrder?.id,
            );
            const free = beleger === null;
            const sel = picked[m.machineNumber];
            return (
              <div className={`rental-pick${sel ? ' is-picked' : ''}`} key={m.machineNumber}>
                <label className={free ? '' : 'is-disabled'}>
                  <input type="checkbox" checked={Boolean(sel)} disabled={!free} onChange={(e) => toggle(m, e.target.checked)} /> {m.machineLabel} {free ? '' : `· belegt (${beleger.number})`}
                </label>
                {sel && (
                  <div className="rental-pick__rates">
                    <label>Tag €<input type="number" min="0" step="0.01" value={sel.dailyRate} onChange={(e) => setRate(m.machineNumber, 'dailyRate', Number(e.target.value) || 0)} /></label>
                    <label>Woche €<input type="number" min="0" step="0.01" value={sel.weeklyRate} onChange={(e) => setRate(m.machineNumber, 'weeklyRate', Number(e.target.value) || 0)} /></label>
                    <label>Monat €<input type="number" min="0" step="0.01" value={sel.monthlyRate} onChange={(e) => setRate(m.machineNumber, 'monthlyRate', Number(e.target.value) || 0)} /></label>
                    <span className="rental-pick__charge" title="Voraussichtliche Miete im Zeitraum">{money(chargeFor(sel, m.minimumDays))}</span>
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
        <div className="rental-modal__toggles">
          <label><input type="checkbox" name="deliveryRequired" defaultChecked /> Auslieferung durch uns</label>
          <label><input type="checkbox" name="pickupRequired" defaultChecked /> Rückholung durch uns</label>
          <label><input type="checkbox" name="kind" value="interval" checked={interval} onChange={(e) => setInterval(e.target.checked)} /> Intervallmiete (wiederkehrend)</label>
        </div>
        {interval && (
          <div className="rental-modal__grid">
            <label>Wochentag<SearchSelect name="intervalWeekday" value={String(weekday)} onChange={(next) => setWeekday(Number(next))} options={weekdayLabels.map((day, index) => ({ value: String(index), label: day }))} /></label>
          </div>
        )}
        {interval
          ? <p className="rental-note">Intervallmiete: die Maschine wird an jedem gewählten Wochentag im Zeitraum zugestellt und wieder abgeholt – berechnet werden nur die Einsatztage (Tagessatz) plus Transportkosten je Fahrt.</p>
          : machines.length > 0 && <p className="rental-modal__total">Voraussichtliche Miete {dateLabel(start)} – {dateLabel(end)}: <strong>{money(estimatedTotal)}</strong> <small>zzgl. USt, ohne Transport/Kaution</small></p>}
        <label className="rental-modal__wide">Notiz<input name="note" defaultValue={editOrder?.note ?? ''} /></label>
        {!editOrder && (
          <div className="rental-modal__toggles">
            <label><input type="checkbox" name="asOffer" checked={asOffer} onChange={(e) => setAsOffer(e.target.checked)} /> Nur als Mietangebot – reserviert die Maschine noch nicht (später bestätigen)</label>
          </div>
        )}
        {error ? <div className="rental-modal__error" role="alert">{error}</div> : null}
        <div className="rental-modal__actions">
          <button type="button" className="rental-btn" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="rental-btn is-pay" disabled={busy}>{editOrder ? 'Speichern' : asOffer ? 'Mietangebot anlegen' : 'Reservieren'}</button>
        </div>
      </form>
    </div>
  );
}

function ReturnModal({ order, busy, onClose, onSubmit }: { readonly order: RentalOrder; readonly busy: boolean; readonly onClose: () => void; readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  return (
    <div className="rental-modal" role="dialog" aria-modal="true" aria-label="Rücknahme erfassen" onMouseDown={(e) => { if (e.target === e.currentTarget && confirmDiscard()) onClose(); }}>
      <form className="rental-modal__panel" onSubmit={onSubmit}>
        <header><Truck size={16} /> <h2>Rücknahme · {order.number}</h2></header>
        <label className="rental-modal__wide">Rücknahmedatum<input name="actualReturnDate" type="date" required defaultValue={TODAY} /></label>
        {order.items.map((item) => (
          <fieldset className="rental-return-item" key={item.id}>
            <legend>{item.machineLabel}</legend>
            <div className="rental-modal__grid">
              <label>Betriebsstunden rein<input name={`meter-${item.id}`} type="number" min="0" defaultValue={item.meterOut ?? ''} /></label>
              <label>Schaden €<input name={`damageAmount-${item.id}`} type="number" min="0" step="0.01" defaultValue="0" /></label>
            </div>
            <label className="rental-modal__wide">Schadenbeschreibung<input name={`damage-${item.id}`} placeholder="keine Schäden" /></label>
          </fieldset>
        ))}
        <div className="rental-modal__actions">
          <button type="button" className="rental-btn" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="rental-btn is-pay" disabled={busy}>Rücknahme abschließen</button>
        </div>
      </form>
    </div>
  );
}
